'use client'

import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { Bot, ChevronUp, Search } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AgentType, AvailableAgents } from 'sprites-types/api/v2/copilot'
import { GoogleIcon, MetaIcon } from '../icons/PlatformIcons'

// Default agent type - should match backend's getDefaultAgentType()
const getDefaultAgentType = (): AgentType => AgentType.META_ADS

interface AgentDropdownProps {
  className?: string
}

// Agent icon mapping
const getAgentIcon = (agent: AgentType) => {
  switch (agent) {
    case AgentType.SEO:
      return <Search className="h-4 w-4" />
    case AgentType.META_ADS:
      return <MetaIcon className="h-4 w-4" />
    case AgentType.GOOGLE_ADS:
      return <GoogleIcon className="h-4 w-4" />
    default:
      return <Bot className="h-4 w-4" />
  }
}

// Get agent display information
const getAgentInfo = (agent: AgentType) => {
  const agentInfo = AvailableAgents.find((a) => a.type === agent)
  return agentInfo || AvailableAgents.find((a) => a.type === getDefaultAgentType())!
}

export default function AgentDropdown({ className }: AgentDropdownProps) {
  const { currentAgent, availableAgents, switchAgent } = useCopilotContext()
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Debug logging to track agent changes
  useEffect(() => {
    console.log('[AgentDropdown] Current agent changed:', {
      currentAgent,
      timestamp: new Date().toISOString(),
    })
  }, [currentAgent])

  const toggleDropdown = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen])

  const handleAgentSwitch = useCallback(
    async (targetAgent: AgentType) => {
      if (targetAgent === currentAgent || isLoading) return

      setIsLoading(true)
      try {
        const success = await switchAgent(targetAgent, 'User manually switched agent')
        if (success) {
          setIsOpen(false) // Close dropdown on successful switch
        }
      } catch (error) {
        console.error('[AgentDropdown] Failed to switch agent:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [currentAgent, switchAgent, isLoading]
  )

  // Close dropdown when clicking outside
  const handleBackdropClick = useCallback(() => {
    if (isOpen && !isLoading) {
      setIsOpen(false)
    }
  }, [isOpen, isLoading])

  const currentAgentInfo = getAgentInfo(currentAgent)

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40 bg-black/10"
            onClick={handleBackdropClick}
          />
        )}
      </AnimatePresence>

      {/* Dropdown Container */}
      <div className={cn('relative', className)} ref={dropdownRef}>
        {/* Dropdown Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.96 }}
              transition={{
                duration: 0.2,
                ease: [0.16, 1, 0.3, 1], // Custom easing for smooth feel
              }}
              className="absolute bottom-full left-0 z-50 mb-2 w-64 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
              style={{
                transformOrigin: 'bottom left',
                backdropFilter: 'blur(8px)',
              }}
            >
              <div className="p-2">
                <div className="mb-2 px-3 py-2 text-xs font-medium text-gray-500">Switch modes</div>
                <div className="space-y-1">
                  {availableAgents.map((agent) => {
                    const agentInfo = getAgentInfo(agent.type)
                    const isSelected = agent.type === currentAgent
                    const isDisabled = isLoading

                    return (
                      <motion.button
                        key={agent.type}
                        onClick={() => handleAgentSwitch(agent.type)}
                        disabled={isDisabled}
                        whileHover={
                          !isSelected && !isDisabled
                            ? { backgroundColor: 'rgb(249 250 251)' }
                            : undefined
                        }
                        whileTap={!isSelected && !isDisabled ? { scale: 0.98 } : undefined}
                        className={cn(
                          'flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
                          {
                            'bg-blue-50 text-blue-700': isSelected,
                            'text-gray-700 hover:bg-gray-50': !isSelected && !isDisabled,
                            'cursor-default opacity-50': isDisabled,
                            'cursor-not-allowed': isSelected,
                          }
                        )}
                      >
                        <div
                          className={cn('mt-0.5 flex-shrink-0', { 'text-blue-500': isSelected })}
                        >
                          {getAgentIcon(agent.type)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{agentInfo.name}</span>
                            {isSelected && <div className="h-2 w-2 rounded-full bg-blue-500" />}
                          </div>
                          <p className="mt-0.5 text-xs text-gray-500">{agentInfo.description}</p>
                        </div>
                      </motion.button>
                    )
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Trigger Button */}
        <motion.button
          onClick={toggleDropdown}
          disabled={isLoading}
          whileHover={{ backgroundColor: 'rgb(224 224 224)' }}
          whileTap={{ scale: 0.95 }}
          className={cn(
            'flex h-8 items-center gap-2 rounded-lg bg-transparent px-3 py-1 text-sm transition-colors',
            'hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
            {
              'cursor-not-allowed opacity-50': isLoading,
              'bg-gray-100': isOpen,
            }
          )}
        >
          <div className="flex-shrink-0 text-gray-600">{getAgentIcon(currentAgent)}</div>
          <span className="line-clamp-1 min-w-0 flex-1 text-left text-gray-700">
            {currentAgentInfo.name}
          </span>
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="flex-shrink-0 text-gray-400"
          >
            <ChevronUp className="h-4 w-4" />
          </motion.div>
          {isLoading && (
            <div className="h-3 w-3 animate-spin rounded-full border border-gray-300 border-t-gray-600" />
          )}
        </motion.button>
      </div>
    </>
  )
}
